import * as actionTypes from "../actionTypes";
import * as binderContentKeys from "../enums/binderContentKeys";
import * as binderOrderItemManip from "../lib/binderOrderItemManip";
import * as binderOrderItemTypes from "../enums/binderOrderItemTypes";
import binderOrderItemFromTab from "../lib/binderOrderItemFromTab";
import createReducer from "../lib/createReducer";
import find from "lodash.find";
import findIndex from "lodash.findindex";
import flatten from "lodash.flatten";

const defaultState = {};

const extractTabIds = (binderId, item) => {
	if (!item.children || !item.children.length) {
		return item.type === binderOrderItemTypes.TAB ? `${binderId}-${item.id}` : null;
	}

	return flatten(item.children.map(orderItem => extractTabIds(binderId, orderItem))).filter(
		tid => tid !== null,
	);
};

function BINDERS_SET(state, action) {
	const { binder, observerIds } = action;

	if (!binder || !observerIds) {
		return state;
	}

	const key = binder.id;
	if (key === undefined || key === null) {
		return state;
	}

	let orderItems = [];
	if (
		binder.content &&
		binder.content[binderContentKeys.ORDER_ITEMS] &&
		binder.content[binderContentKeys.ORDER_ITEMS].length
	) {
		orderItems = binder.content[binderContentKeys.ORDER_ITEMS];
	} else if (binder.tabs) {
		orderItems = binder.tabs.map((tab, i) => binderOrderItemFromTab(tab, i));
	}

	return {
		...state,
		[key]: {
			accessCount: binder.access_count,
			alertCount: binder.alert_count,
			alertHistory: binder.alert_history,
			alertLastTime: binder.alert_last_time,
			alertRate: binder.alert_rate,
			alertStartTime: binder.alert_start_time,
			bookmarked: binder.bookmarked,
			clientcode: binder.clientcode,
			content: binder.content,
			createTime: binder.create_time,
			description: binder.description,
			dirty: false,
			editable: binder.editable,
			id: binder.id,
			libraryName: binder.library_name,
			links: binder.links,
			observerIds: observerIds,
			observers: binder.observers,
			orderItems,
			ownerEmail: binder.owner_email,
			ownerName: binder.owner_name,
			ownerUserId: binder.owner_user_id,
			shareable: binder.shareable,
			shareTime: binder.share_time,
			tabCount: binder.tab_count,
			tabIds: binder.tabs ? binder.tabs.map((t, index) => `${binder.id}-${index}`) : null,
			title: binder.title,
			type: binder.type,
			updateTime: binder.update_time,
			viewTime: binder.view_time,
		},
	};
}

function BINDERS_SET_MULTIPLE(state, action) {
	const { binders } = action;
	if (!binders || !binders.length) {
		return state;
	}

	let nextState = state;
	binders.forEach(binder => {
		nextState = BINDERS_SET(nextState, binder);
	});

	return nextState;
}

function BINDERS_SAVE(state, action) {
	const { binderId } = action;
	const binder = state[binderId];

	if (!binder) {
		return state;
	}

	return {
		...state,
		[binderId]: {
			...binder,
			dirty: false,
		},
	};
}

function BINDER_TABS_SET(state, action) {
	const { tabs } = action;
	if (tabs.length === 0) {
		return state;
	}

	const nextState = { ...state };
	let foundNewTab = false;
	tabs.forEach(t => {
		const { binderId, id } = t;

		const checkedOrderItem = t.generatedFromIframe ? binderOrderItemFromTab(t, id, "/img/analytics-icon.svg") : binderOrderItemFromTab(t, id)

		if (binderId !== undefined && binderId !== null && id !== undefined && id !== null) {
			const binder = nextState[binderId];
			if (binder) {
				const hasTab = find(binder.tabIds, tabId => tabId === `${binderId}-${id}`);
				if (!hasTab) {
					foundNewTab = true;
					nextState[binderId] = {
						...binder,
						tabIds: [...binder.tabIds, `${binderId}-${id}`],
						orderItems: [...binder.orderItems, checkedOrderItem],
						tabCount: binder.tabCount + 1,
					};
				}
			} else {
				if (__DEV__) {
					console.warn("Ignoring attempt to add a tab to a binder that doesn't exist.");
				}
			}
		}
	});

	return foundNewTab ? nextState : state; // Return original state if no new tabs were added
}

function BINDER_TABS_REMOVE(state, action) {
	const { binderId, tabId } = action;
	if (binderId === null || binderId === undefined || tabId === null || tabId === undefined) {
		return state;
	}

	const binder = state[binderId];
	if (binderId === null || binderId === undefined) {
		if (__DEV__) {
			console.warn("Attempting to remove tab from a binder that does not exist.");
		}
		return state;
	}

	const nextState = { ...state };
	const tabIds = binder.tabIds.filter(tid => tid !== `${binderId}-${tabId}`);
	const orderItems = binderOrderItemManip.remove(binder.orderItems, tabId);

	nextState[binderId] = {
		...binder,
		tabIds,
		orderItems,
		tabCount: tabIds.length,
		dirty: true,
	};

	return nextState;
}

function BINDERS_REMOVE(state, action) {
	const { binderId } = action;
	if (binderId === undefined || binderId === null || !state[binderId]) {
		return state;
	}

	const nextState = { ...state };
	delete nextState[binderId];
	return nextState;
}

function BINDERS_SET_OWNER(state, action) {
	const { binderId, ownerName, ownerEmail, ownerUserId } = action;
	if (
		binderId === undefined ||
		binderId === null ||
		!state[binderId] ||
		!ownerName ||
		!ownerEmail ||
		ownerUserId === undefined ||
		ownerUserId === null
	) {
		return state;
	}

	return {
		...state,
		[binderId]: {
			...state[binderId],
			ownerName: ownerName,
			ownerEmail: ownerEmail,
			ownerUserId: ownerUserId,
			dirty: true,
		},
	};
}

function BINDERS_SET_EDITABLE(state, action) {
	const { binderId, editable } = action;

	if (
		editable === undefined ||
		editable === null ||
		binderId === undefined ||
		binderId === null
	) {
		return state;
	}

	return {
		...state,
		[binderId]: {
			...state[binderId],
			editable,
			dirty: true,
		},
	};
}

function BINDERS_SET_BOOKMARKED(state, action) {
	const { binderId, bookmarked } = action;

	if (
		bookmarked === undefined ||
		bookmarked === null ||
		binderId === undefined ||
		binderId === null
	) {
		return state;
	}

	return {
		...state,
		[binderId]: {
			...state[binderId],
			bookmarked,
		},
	};
}

function BINDERS_SET_ALERT_RATE(state, action) {
	const { binderId, alertRate } = action;

	if (
		alertRate === undefined ||
		alertRate === null ||
		binderId === undefined ||
		binderId === null
	) {
		return state;
	}

	return {
		...state,
		[binderId]: {
			...state[binderId],
			alertRate,
		},
	};
}

function BINDERS_SET_ALERT_HISTORY(state, action) {
	const { binderId, alertHistory } = action;

	if (
		alertHistory === undefined ||
		alertHistory === null ||
		binderId === undefined ||
		binderId === null
	) {
		return state;
	}

	return {
		...state,
		[binderId]: {
			...state[binderId],
			alertHistory,
		},
	};
}

function BINDERS_SET_TITLE(state, action) {
	const { binderId, title } = action;

	if (binderId === undefined || binderId === null) {
		return state;
	}

	return {
		...state,
		[binderId]: {
			...state[binderId],
			title,
			dirty: true,
		},
	};
}

function BINDERS_SET_CLIENT_CODE(state, action) {
	const { binderId, clientcode } = action;

	if (binderId === undefined || binderId === null) {
		return state;
	}

	return {
		...state,
		[binderId]: {
			...state[binderId],
			clientcode,
			dirty: true,
		},
	};
}

function BINDERS_SET_DESCRIPTION(state, action) {
	const { binderId, description } = action;

	if (binderId === undefined || binderId === null) {
		return state;
	}

	return {
		...state,
		[binderId]: {
			...state[binderId],
			description,
			dirty: true,
		},
	};
}

function BINDERS_REORDER_ITEMS(state, action) {
	const { binderId, orderItems } = action;

	if (
		binderId === undefined ||
		binderId === null ||
		orderItems === undefined ||
		orderItems === null
	) {
		return state;
	}

	const binder = state[binderId];
	if (!binder) {
		return state;
	}

	const tabIds = extractTabIds(binderId, { children: orderItems });

	return {
		...state,
		[binderId]: {
			...binder,
			tabIds: tabIds,
			orderItems,
			dirty: true,
		},
	};
}

function BINDERS_SET_OBSERVERS(state, action) {
	const { binderId, observerIds } = action;

	if (
		binderId === undefined ||
		binderId === null ||
		observerIds === undefined ||
		observerIds === null
	) {
		return state;
	}

	const binder = state[binderId];
	if (!binder) {
		return state;
	}

	return {
		...state,
		[binderId]: {
			...binder,
			observerIds,
		},
	};
}

function BINDERS_ADD_OBSERVER(state, action) {
	const { binderId, observerId } = action;

	if (
		binderId === undefined ||
		binderId === null ||
		observerId === undefined ||
		observerId === null
	) {
		return state;
	}

	const binder = state[binderId];
	if (!binder) {
		return state;
	}

	return {
		...state,
		[binderId]: {
			...binder,
			observerIds: [...binder.observerIds, observerId],
		},
	};
}

function BINDERS_REMOVE_OBSERVER(state, action) {
	const { binderId, observerId } = action;

	if (
		binderId === undefined ||
		binderId === null ||
		observerId === undefined ||
		observerId === null
	) {
		return state;
	}

	const binder = state[binderId];
	if (!binder || !find(binder.observerIds, id => id === observerId)) {
		return state;
	}

	const observerIdIndex = findIndex(binder.observerIds, id => id === observerId);
	if (observerIdIndex === -1) {
		return state;
	}

	return {
		...state,
		[binderId]: {
			...binder,
			observerIds: [
				...binder.observerIds.slice(0, observerIdIndex),
				...binder.observerIds.slice(observerIdIndex + 1),
			],
		},
	};
}

function BINDER_ADD_ORDER_ITEM(state, action) {
	const { id, insertAfterId, itemType, label, binderId, children } = action;

	if (
		binderId === null ||
		binderId === undefined ||
		id === null ||
		id === undefined ||
		!label ||
		!itemType
	) {
		return state;
	}

	const binder = state[binderId];
	if (!binder) {
		return state;
	}

	const newChildren = children ? children : [];
	let tabIds;
	let orderItems;
	if (insertAfterId === null || insertAfterId === undefined) {
		tabIds =
			itemType === binderOrderItemTypes.TAB
				? [...binder.tabIds, `${binderId}-${id}`]
				: binder.tabIds;
		orderItems = [
			...binder.orderItems,
			{
				id,
				type: itemType,
				label,
				children: newChildren,
			},
		];
	} else {
		const newOrderItem = {
			label,
			id,
			type: itemType,
			children: newChildren,
		};

		orderItems = binderOrderItemManip.insert(binder.orderItems, insertAfterId, newOrderItem);
		tabIds = extractTabIds(orderItems);
	}

	return {
		...state,
		[binderId]: {
			...binder,
			tabIds,
			orderItems,
			dirty: true,
		},
	};
}

function BINDER_ADD_FOLDER_WITH_TABS(state, action) {
	const { binderId, label, itemId, childrenTabs } = action;
	const nextState = { ...state };
	const binder = nextState[binderId];

	if (childrenTabs.length === 0 || binderId === undefined || binderId === null || !binder) {
		return state;
	}

	const orderItems = [];
	const folderOrderItem = { label, id: itemId, type: binderOrderItemTypes.FOLDER };
	binder.orderItems.forEach(i => {
		const hasItem = find(childrenTabs, tab => tab.id === i.id);
		if (!hasItem) {
			orderItems.push(i);
		}
	});
	const children = childrenTabs
		.map(tab => {
			return tab.id !== undefined && tab.id !== null
				? binderOrderItemFromTab(tab, tab.id)
				: null;
		})
		.filter(tab => !!tab);
	folderOrderItem.children = children;
	nextState[binderId] = {
		...binder,
		orderItems: [...orderItems, folderOrderItem],
	};

	if (children.length !== 0) {
		return nextState;
	}

	return state;
}

function BINDERS_RELABEL_FOLDER(state, action) {
	const { folderId, folderLabel, binderId } = action;
	if (
		binderId === null ||
		binderId === undefined ||
		folderId === null ||
		folderId === undefined ||
		!folderLabel
	) {
		return state;
	}

	const binder = state[binderId];
	if (!binder) {
		return state;
	}

	const nextOrderItems = binderOrderItemManip.modify(binder.orderItems, folderId, folder => ({
		...folder,
		label: folderLabel,
	}));
	return {
		...state,
		[binder.id]: {
			...binder,
			orderItems: nextOrderItems,
			dirty: true,
		},
	};
}

const handlers = {};
handlers[actionTypes.BINDERS_SET] = BINDERS_SET;
handlers[actionTypes.BINDERS_SET_MULTIPLE] = BINDERS_SET_MULTIPLE;
handlers[actionTypes.BINDER_TABS_SET] = BINDER_TABS_SET;
handlers[actionTypes.BINDER_TABS_REMOVE] = BINDER_TABS_REMOVE;
handlers[actionTypes.BINDERS_REMOVE] = BINDERS_REMOVE;
handlers[actionTypes.BINDERS_SAVE] = BINDERS_SAVE;
handlers[actionTypes.BINDERS_SET_OWNER] = BINDERS_SET_OWNER;
handlers[actionTypes.BINDERS_SET_EDITABLE] = BINDERS_SET_EDITABLE;
handlers[actionTypes.BINDERS_SET_BOOKMARKED] = BINDERS_SET_BOOKMARKED;
handlers[actionTypes.BINDERS_SET_ALERT_RATE] = BINDERS_SET_ALERT_RATE;
handlers[actionTypes.BINDERS_SET_ALERT_HISTORY] = BINDERS_SET_ALERT_HISTORY;
handlers[actionTypes.BINDERS_SET_TITLE] = BINDERS_SET_TITLE;
handlers[actionTypes.BINDERS_SET_CLIENT_CODE] = BINDERS_SET_CLIENT_CODE;
handlers[actionTypes.BINDERS_SET_DESCRIPTION] = BINDERS_SET_DESCRIPTION;
handlers[actionTypes.BINDERS_REORDER_ITEMS] = BINDERS_REORDER_ITEMS;
handlers[actionTypes.BINDERS_SET_OBSERVERS] = BINDERS_SET_OBSERVERS;
handlers[actionTypes.BINDER_ADD_ORDER_ITEM] = BINDER_ADD_ORDER_ITEM;
handlers[actionTypes.BINDER_ADD_FOLDER_WITH_TABS] = BINDER_ADD_FOLDER_WITH_TABS;
handlers[actionTypes.BINDERS_RELABEL_FOLDER] = BINDERS_RELABEL_FOLDER;
handlers[actionTypes.BINDERS_ADD_OBSERVER] = BINDERS_ADD_OBSERVER;
handlers[actionTypes.BINDERS_REMOVE_OBSERVER] = BINDERS_REMOVE_OBSERVER;
export default createReducer(defaultState, handlers);
